import * as React from 'react'
import styled from 'styled-components'

import { HorizontalAlignment, i18n } from '@thg-commerce/enterprise-core'
import {
  Product,
  ProductImage,
  ProductVariant,
  ShopTheLook,
  ShopTheLookHotSpot,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  HotSpots,
  ShopTheLook as ShopTheLookWidget,
  ShopTheLookProps,
} from '@thg-commerce/enterprise-widget-shop-the-look'
import { spacing } from '@thg-commerce/gravity-theme'

import { BaseWidgetProps, ProductVariantItem } from '../types'

export type BannersProps = Omit<ShopTheLookHotSpot, 'id' | 'query' | 'SKU'> & {
  SKU?: SkuProps
}

export type HotSpotProductsProps = HotSpots[]

export type ProductProps = Omit<
  Product,
  | 'alternateLinks'
  | 'content'
  | 'options'
  | 'preorder'
  | 'variants'
  | 'reviews'
  | 'images'
  | 'sku'
  | 'title'
  | 'eligibleForWishlist'
  | 'leadTime'
  | 'weightGroups'
  | 'price'
  | 'cheapestVariant'
> & {
  reviews?: {
    averageScore: number
    total: number
  }
  price?: Pick<ProductVariant, 'price'>
  cheapestVariant?: Pick<ProductVariant, 'price'>
  images: ProductImage[]
}

export type SkuProps = Omit<
  ProductVariant,
  | 'availabilityMessage'
  | 'choices'
  | 'clickAndCollectStores'
  | 'content'
  | 'eligibleForFulfilmentMethods'
  | 'inStock'
  | 'isAutoRenewSubscription'
  | 'isSubscription'
  | 'subscriptionContracts'
  | 'product'
  | 'isCheckStock'
  | 'isOrderInStore'
  | 'eligibleForWishlist'
  | 'leadTime'
  | 'weightGroups'
> &
  ProductVariantItem & {
    product?: ProductProps
    productDescription?: string
  }

export type ShopTheLookWidgetRendererProps = BaseWidgetProps &
  Omit<ShopTheLook, 'id' | 'query' | 'banners'> & {
    hotSpotVariants?: HotSpotProductsProps
    primaryColor?: boolean
    horizontalAlignment?: HorizontalAlignment
  }

const StyledShopTheLookWidget = styled(ShopTheLookWidget)`
  margin: 0 auto;
  padding: ${spacing(2)} 0;
  max-width: ${(props) => props.theme.site.siteWidth};
`

export const ShopTheLookWidgetRenderer = (
  props: ShopTheLookWidgetRendererProps,
) => {
  if (!props.hotSpotVariants || props.hotSpotVariants.length === 0) {
    return null
  }

  const rrpText = i18n('product.price.rrp.text')
  const shopTheLookWidgetProps: ShopTheLookProps = {
    title: props.itemTitle || undefined,
    description: props.itemDescription || undefined,
    horizontalAlignment: props.horizontalAlignment,
    image: {
      urls: {
        small: props.imageSmall,
        medium: props.imageMedium,
        large: props.imageLarge,
      },
      alt: props.altImageLarge || undefined,
    },
    iconPrimaryColor: props.primaryColor || true,
    hotSpots: props.hotSpotVariants.map((hotSpot) => {
      return {
        ...hotSpot,
        hotSpotProduct: {
          ...hotSpot.hotSpotProduct,
          i18nText: {
            closeI18nText: {
              closeAriaLabel: 'close',
              closeLabel: 'close',
            },
            freeGiftMessage: 'freegift',
          },
          price: { rrpText, ...hotSpot.hotSpotProduct?.price },
        },
      }
    }) as HotSpots[],
  }

  return <StyledShopTheLookWidget {...shopTheLookWidgetProps} />
}
